import { GET, MPUT } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { type NOTIFICATION_TYPE, NOTIFICATION_TYPES } from 'types/Notification.ts';
import type { UserSettings as TUserSettings } from 'types/UserSettings.ts';
import { IBell } from 'ui/component/Icons.tsx';
import { Toggle } from 'ui/control/Toggle.tsx';
import { DateFormatSelector } from '../component/DateFormatSelector.tsx';

type NotificationSettings = {
  [K in NOTIFICATION_TYPE]: boolean;
};

const NotificationToggle: React.FC<{ type: NOTIFICATION_TYPE }> = ({ type }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between">
      <span className="text-grey-label">{t(`settings.notifications.types.${type}`)}</span>
      <Toggle name={type} />
    </div>
  );
};

export const Settings: React.FC = () => {
  const { t } = useTranslation();
  const formRef = React.useRef<HTMLFormElement>(null);

  const currentSettingsSwr = useSWR<TUserSettings>(['/user-settings'], GET);
  const saveMutation = useSWRMutation('/user-settings', MPUT);

  const defaultValues: NotificationSettings = React.useMemo(
    () => Object.fromEntries(NOTIFICATION_TYPES.map((type) => [type, currentSettingsSwr.data?.subscribed_types?.includes(type) ?? false])) as NotificationSettings,
    [currentSettingsSwr.data],
  );

  const formContext = useForm({
    mode: 'onChange',
    defaultValues,
    values: defaultValues,
  });

  const { handleSubmit } = formContext;

  const onSubmit = async (data: NotificationSettings) => {
    await saveMutation.trigger({
      subscribed_types: Object.entries(data)
        .filter(([_, enabled]) => enabled)
        .map(([type]) => type),
      shipments_silenced: currentSettingsSwr.data?.shipments_silenced ?? [],
    });
    void currentSettingsSwr.mutate();
  };

  return (
    <main className="view-container">
      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('settings.title', 'Settings')}</h4>
      </section>
      <section className="mt-4 flex flex-col gap-6 bg-white p-8">
        <div className="flex flex-row gap-6">
          <div className="flex w-full flex-col gap-4">
            <h4>{t('settings.date.title', 'Date Settings')}</h4>
            <p>{t('settings.date.description', 'Choose which date format you want to use.')}</p>
            <div className="flex w-full flex-row gap-2">
              <DateFormatSelector />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <h4>{t('settings.notifications.title', 'Notification Settings')}</h4>
            <p>{t('settings.notifications.description', 'Choose which notifications you want to receive.')}</p>
            <div className="flex items-center gap-2 text-blue">
              <IBell className="h-5 w-5" />
              <span className="text-sm">{t('settings.notifications.tip')}</span>
            </div>
          </div>
          <FormProvider {...formContext}>
            <form ref={formRef} className="flex max-w-xl flex-col gap-6" onChange={handleSubmit(onSubmit)} noValidate={true}>
              {NOTIFICATION_TYPES.map((type) => (
                <NotificationToggle key={type} type={type} />
              ))}
              {saveMutation.error && <p className="text-red">{t('settings.notifications.error', 'Failed to save notification settings')}</p>}
            </form>
          </FormProvider>
        </div>
      </section>
    </main>
  );
};
